import { useState } from 'react';
import { Tooltip } from '@qodo/design-system';
import { useAccountInfo } from 'hooks/billing/useAccountInfo';
import { t } from 'i18next';
import { QodoAnteater, QodoText } from 'logos';
import {
  SidebarHeader,
  SidebarMenuButton,
  SidebarTrigger,
  useSidebar,
} from 'ui/sidebar';
import { isMigratedPaidUser } from 'utils/billing/conversion';
import { mascotMessageKey } from 'utils/billing/mascotMessages';
import { trialDayBand } from 'utils/billing/trialDayBand';
import { cn } from 'utils/className';
import { isOnboardingPath } from 'utils/routes';
import { spawn } from 'utils/translations';

const spawnMascot = spawn('billing.mascot');

// ── Mascot talk animation toggle (PLT-4058) ──
// Single switch for the ~0.7s "starting to talk" nod on the logo. To remove the
// animation entirely: set this to '' and delete the `mascot-talk` block in
// css/base/animate.css. Nothing else references it.
const TALK_ANIMATION_CLASS = 'mascot-talk';

// Collapsed rail: the logo and the expand toggle share one grid cell and
// cross-fade. Opacity (not `hidden`/`display:none`) keeps the toggle in the tab
// order and accessibility tree, so keyboard and screen-reader users can reach
// it. Two independent reveal triggers:
//
// - HOVER is sidebar-wide: hovering anywhere on the collapsed rail reveals the
//   toggle. The unnamed `group-hover` matches the sidebar wrapper (ui/sidebar
//   carries an unnamed `group` on the rail), which is the whole hover target.
// - KEYBOARD focus is local: `group-has-[:focus-visible]` scoped to this
//   container (`group/logo-toggle`) reveals the toggle only when the toggle
//   itself is keyboard-focused — not when tabbing through other nav items.
//
// `focus-visible` (not `focus-within`) is deliberate: collapsing is a click on
// the toggle, which keeps DOM focus on that same re-used button. `focus-within`
// would leave the toggle stuck open with the logo hidden; `focus-visible`
// excludes mouse clicks so only keyboard focus reveals it.
const COLLAPSED_GROUP_CLASS = 'group/logo-toggle';
const COLLAPSED_LOGO_CLASS =
  'transition-opacity group-hover:opacity-0 group-has-[:focus-visible]/logo-toggle:opacity-0';
const COLLAPSED_TRIGGER_CLASS =
  'opacity-0 transition-opacity group-hover:opacity-100 group-has-[:focus-visible]/logo-toggle:opacity-100';

/**
 * Whether the user prefers reduced motion. When true the `mascot-talk` CSS
 * animation is disabled (see css/base/animate.css), so `onAnimationEnd` would
 * never fire and the talking state would stick on — we skip animating instead.
 */
const prefersReducedMotion = (): boolean =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * The existing top-left Qodo logo mark, with a state-aware witty tooltip during
 * an active self-serve trial (PLT-4058). Outside a trial it renders exactly as
 * before — no tooltip, no animation, no layout change.
 */
function LogoMark() {
  const { data: accountInfo } = useAccountInfo();
  const [talking, setTalking] = useState(false);

  const trial = accountInfo?.trial ?? null;
  const isPaidMigration = isMigratedPaidUser(accountInfo);

  const message = isPaidMigration
    ? spawnMascot('migration')
    : trial?.active
      ? spawnMascot(mascotMessageKey(trialDayBand(trial), trial.dayNumber))
      : null;

  if (!message) {
    return <QodoAnteater className="size-8!" />;
  }

  const startTalking = (): void => {
    if (!prefersReducedMotion()) {
      setTalking(true);
    }
  };

  const stopTalking = (): void => setTalking(false);

  return (
    <Tooltip
      content={message}
      contentProps={{
        side: 'right',
        align: 'center',
        // Mascot speech bubble = the Qodo brand purple (Figma node 3244-23862,
        // fill #7968FA — same brand purple as the logo + the status-pill dot).
        // The DS tooltip's default arrow keeps its own muted-purple fill
        // (bg/fill-purple-muted-700), so without overriding it the tail renders
        // as a darker "black diamond" that mismatches the bubble (PLT-4157). The
        // `[&_svg]` rules recolor the Radix arrow to the same brand purple — a
        // DESCENDANT selector because Radix nests the arrow as content > span >
        // svg, so a direct-child `[&>svg]` would miss it.
        // Raw billing-brand hex is the documented DS-token exception (PLT-4095),
        // matching the BillingStatusPill dot.
        className:
          'bg-[#7968FA] border-[#7968FA] text-primary-foreground [&_svg]:!bg-[#7968FA] [&_svg]:!fill-[#7968FA]',
      }}
      triggerProps={{ asChild: true }}
    >
      <span
        data-testid="trial-mascot"
        onMouseEnter={startTalking}
        onAnimationEnd={stopTalking}
        className={cn('inline-flex', talking && TALK_ANIMATION_CLASS)}
      >
        <QodoAnteater className="size-8!" />
      </span>
    </Tooltip>
  );
}

export function LogoHeader({
  className,
  onClick = () => {},
  ...rest
}: React.ComponentProps<typeof SidebarHeader>) {
  const { state, openMobile } = useSidebar();

  const isExpanded = state === 'expanded' || openMobile;
  const canToggle = !isOnboardingPath();

  const handleLogoClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(e as unknown as React.MouseEvent<HTMLDivElement>);
    }
  };

  const sidebarTrigger = canToggle && (
    <Tooltip
      content={t('header.sidebar-toggle-button.tooltip')}
      contentProps={{
        side: 'right',
        align: 'center',
      }}
      triggerProps={{
        asChild: true,
        className: 'z-50 flex items-center p-2',
      }}
    >
      <SidebarTrigger
        className={cn({ [COLLAPSED_TRIGGER_CLASS]: !isExpanded })}
      />
    </Tooltip>
  );

  return (
    <SidebarHeader
      className={cn(
        'py-0 px-2 mb-2 h-(--main-header-height) justify-center',
        className,
        {
          'px-4': isExpanded,
        },
      )}
      {...rest}
    >
      <div
        className={cn({
          'flex items-center justify-between': isExpanded,
          [`${COLLAPSED_GROUP_CLASS} grid place-items-center *:[grid-area:1/1]`]:
            !isExpanded,
        })}
      >
        {isExpanded && (
          <SidebarMenuButton
            className="gap-1 hover:bg-transparent w-fit p-0"
            size="lg"
            onClick={handleLogoClick}
          >
            <LogoMark />
            <QodoText className="size-14!" />
          </SidebarMenuButton>
        )}
        {!isExpanded && (
          <span
            className={cn('flex items-center', {
              [COLLAPSED_LOGO_CLASS]: canToggle,
            })}
          >
            <LogoMark />
          </span>
        )}
        {sidebarTrigger}
      </div>
    </SidebarHeader>
  );
}
